import { useAuthoringEffort, type AuthoringEffort } from '@/features/coaching-effort/useAuthoringEffort';
import { AuthoringEffortControls } from '@/features/coaching-effort/AuthoringEffortControls';
import type { EditCategory } from '@/features/coaching-effort/EffortSession';
import {
  confirmationFactSchema,
  humanConfirmationFact,
  type ConfirmationForm,
} from "./confirmationFact";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/Button";
import { TkticDateInput } from "@/components/ui/TkticDateInput";
import { useAuthStore } from "@/stores/auth";
import { TkticSelect } from "@/components/ui/TkticSelect";
import {
  ComposerPlayerPicker,
} from "@/features/coach-review/components/Composer";
import { GoalWritingFields } from "@/features/coach/components/GoalWritingFields";
import { CoachModal } from "@/features/coach/components/CoachModal";
import { ChevronRight } from "lucide-react";
import { clearOwnedAssistState, useOwnedAssistState } from '@/features/coach/assist/ownedAssistState';
import { useRoster } from "@/features/coach/hooks/useRoster";
import {
  draftContextKey,
  isCurrentDraftContext,
  useComposerDraftContext,
  type DraftContext,
} from "@/features/coach-review/composerDrafts";
import { draftJobMessage, publicationVisibility } from '@/features/coach/assist/assistPresentation';
import { ApiError, errorMessage } from "@/lib/api";
import type {
  ApproveConfirmedCoachingDraftRequest,
  ApprovePrivateCoachingFeedbackRequest,
  ApproveCoachingFeedbackRequest,
  ApproveCoachingGoalRequest,
  CoachingFeedbackAcceptedResponse,
  CanonicalCoachingGoalContent,
  PrivateCoachingFeedbackAcceptedResponse,
  CoachingApplicationDraft,
  CoachingAIJobProgress,
  CoachingConfirmationResponse,
  CoachingPublicationPreview,
  CoachingPublicationSelection,
  CreateCoachingConfirmationRequest,
  DiscardCoachingCaptureRequest,
  GenerateConfirmedCoachingDraftRequest,
} from "@/lib/types";
import { ProposalEvidence } from "./ProposalEvidence";
import { useDraftInbox } from "./useDraftInbox";
import {
  boundedHumanEdits,
  clearReviewCarryForOrigin,
  clearReviewCarryForCapture,
  FrozenOperation,
  proposedWriting,
  readReviewCarry,
  recipientsReviewable,
  saveReviewCarry,
} from "./reviewState";
import { decodeCanonicalFeedbackReviewCarry, decodeCoachingFeedbackReceipt, decodeCoachingGoalReceipt, type CanonicalFeedbackReviewCarry } from './canonicalFeedbackReview';
import type { SavedPublicFeedbackRequest } from '@/features/feedback-integration/resolvePublishedAssistFeedback';
import type { SavedPrivateFeedbackRequest } from "@/features/feedback-integration/resolveSavedPrivateFeedback";
import {
  classifyPrivateApprovalFailure,
  decodePrivateFeedbackReceipt,
  decodePrivateFeedbackReviewCarry,
  type PrivateApprovalFailure,
  type PrivateFeedbackReviewCarry,
} from "./privateFeedbackReview";
import { feedbackProseLength, feedbackProseLimits, isFeedbackProseWithinLimit } from "@/lib/feedbackAuthoringContinuation";
import type { ReviewOrigin } from "./reviewState";

export function DraftInbox({
  onManualFeedback,
  visible = true,
  embedded = false,
  captureId,
  selectionIntent = 0,
  onOpenSavedFeedback,
  onOpenPublishedFeedback,
}: {
  onManualFeedback: () => void;
  visible?: boolean;
  embedded?: boolean;
  captureId?: string | null;
  selectionIntent?: number;
  onOpenSavedFeedback?: (request: SavedPrivateFeedbackRequest, signal: AbortSignal) => Promise<void>;
  onOpenPublishedFeedback?: (request: SavedPublicFeedbackRequest, signal: AbortSignal) => Promise<void>;
}) {
  const owner = useComposerDraftContext();
  return owner ? (
    <OwnedInbox
      key={draftContextKey(owner)}
      owner={owner}
      onManualFeedback={onManualFeedback}
      visible={visible}
      embedded={embedded}
      requestedCaptureId={captureId}
      selectionIntent={selectionIntent}
      onOpenSavedFeedback={onOpenSavedFeedback}
      onOpenPublishedFeedback={onOpenPublishedFeedback}
    />
  ) : null;
}
type ApprovalOperation = {
  captureId: string;
  draftId: string;
  body: ApproveConfirmedCoachingDraftRequest | ApprovePrivateCoachingFeedbackRequest | ApproveCoachingFeedbackRequest | ApproveCoachingGoalRequest;
  metricLabels?: EditCategory[] | null;
};
function isPrivateOperation(request: ApprovalOperation): request is ApprovalOperation & { body: ApprovePrivateCoachingFeedbackRequest } {
  return "schema_version" in request.body && request.body.schema_version === "coaching-private-feedback-approval.v1";
}
function isTaggedOperation(request: ApprovalOperation) { return 'schema_version' in request.body; }
function validatedReceipt(request: ApprovalOperation, result: unknown) {
  if (!('schema_version' in request.body)) return null;
  const receipt = request.body.schema_version === 'coaching-private-feedback-approval.v1' ? decodePrivateFeedbackReceipt(result)
    : request.body.schema_version === 'coaching-feedback-approval.v1' ? decodeCoachingFeedbackReceipt(result) : decodeCoachingGoalReceipt(result);
  if (!receipt) throw new Error('Approval result could not be verified. Retry the unchanged operation.');
  return receipt;
}
function dispatchApproval(flow: ReturnType<typeof useDraftInbox>, request: ApprovalOperation) {
  const body = request.body;
  if ('schema_version' in body) {
    if (body.schema_version === 'coaching-private-feedback-approval.v1') return flow.approvePrivate(request.draftId, body);
    if (body.schema_version === 'coaching-feedback-approval.v1') return flow.approveFeedback(request.draftId, body);
    return flow.approveGoal(request.draftId, body);
  }
  // Retained historical intent only. Fresh approvals use the tagged families.
  return flow.approve(request.draftId, body);
}
function approvalOperationKey(request: ApprovalOperation): string {
  return JSON.stringify([
    request.captureId,
    request.draftId,
    request.body.client_operation_id,
  ]);
}
function sameApprovalOperation(
  left: ApprovalOperation | null,
  right: ApprovalOperation,
): boolean {
  return !!left && approvalOperationKey(left) === approvalOperationKey(right);
}
type GenerationOperation = { id: string; body: GenerateConfirmedCoachingDraftRequest };
type GenerationRejection = 'policy_unavailable' | 'provider_not_configured' | 'provider_secret_missing' | 'provider_unsupported'
  | 'confirmation_revision_conflict' | 'publication_preview_stale' | 'occupancy_unavailable';
type GenerationHistory = {
  operationId: string;
  inFlight: number;
  mayHaveAdmitted: boolean;
  rejection: GenerationRejection | null;
};
function generationRejection(error: unknown): GenerationRejection | null {
  if (!(error instanceof ApiError) || typeof error.detail !== 'object' || error.detail === null || !('code' in error.detail)) return null;
  const code = error.detail.code;
  // Capacity rejection rolls back the admission transaction, including its
  // enqueue and reservation. It cannot settle an earlier uncertain attempt.
  if (error.status === 429 && code === 'occupancy_unavailable') return code;
  // These exact errors precede job lookup/reservation. A later such error
  // cannot disprove admission by an earlier unresolved attempt.
  if (error.status === 409 && (code === 'confirmation_revision_conflict' || code === 'publication_preview_stale')) return code;
  return error.status === 503 && (code === 'policy_unavailable' || code === 'provider_not_configured' || code === 'provider_secret_missing' || code === 'provider_unsupported') ? code : null;
}
function OwnedInbox({
  owner,
  onManualFeedback,
  visible,
  embedded,
  requestedCaptureId,
  selectionIntent,
  onOpenSavedFeedback,
  onOpenPublishedFeedback,
}: {
  owner: DraftContext;
  onManualFeedback: () => void;
  visible: boolean;
  embedded: boolean;
  requestedCaptureId?: string | null;
  selectionIntent: number;
  onOpenSavedFeedback?: (request: SavedPrivateFeedbackRequest, signal: AbortSignal) => Promise<void>;
  onOpenPublishedFeedback?: (request: SavedPublicFeedbackRequest, signal: AbortSignal) => Promise<void>;
}) {
  const [open, setOpen] = useState(embedded);
  const [publishedReceipt, setPublishedReceipt] = useState<string | null>(null);
  const [privateReceipt, setPrivateReceipt] = useOwnedAssistState<PrivateCoachingFeedbackAcceptedResponse | null>(owner, 'inbox:private-accepted', null);
  const [privateResultOpen, setPrivateResultOpen] = useState(false);
  const [publicReceipt, setPublicReceipt] = useOwnedAssistState<CoachingFeedbackAcceptedResponse | null>(owner, 'inbox:public-accepted', null);
  const [publicResultOpen, setPublicResultOpen] = useState(false);
  const [approvalFailures] = useOwnedAssistState(owner, 'inbox:private-failures', new Map<string, PrivateApprovalFailure>());
  const [approvalInFlight] = useOwnedAssistState(owner, 'inbox:private-in-flight', new Map<string, number>());
  const [pendingApproval] = useOwnedAssistState(owner, 'inbox:approval-pending', { current: null as ApprovalOperation | null });
  const [pendingApprovals] = useOwnedAssistState(owner, 'inbox:approval-receipts', { current: new Map<string, ApprovalOperation>() });
  const [captureId, setCaptureId] = useState<string | null>(
    () => requestedCaptureId ?? pendingApproval.current?.captureId ?? (embedded ? null : readReviewCarry(owner)?.origin.captureId ?? null),
  );
  const [reviewOpen, setReviewOpen] = useState(!!captureId);
  const [visited, setVisited] = useState<string[]>(captureId ? [captureId] : []);
  const flow = useDraftInbox(owner, null, visible && open);
  const effort = useAuthoringEffort(owner, visible && open && reviewOpen && !!captureId);
  const roster = useRoster();
  const [reconciliationError, setReconciliationError] = useState<{
    operationKey: string;
    message: string;
  } | null>(null);
  const [reconcilingOperationKey, setReconcilingOperationKey] = useState<
    string | null
  >(null);
  const lifetime = useRef(true);
  const visibleReview = useRef({ captureId, active: visible && open && reviewOpen });
  visibleReview.current = { captureId, active: visible && open && reviewOpen };
  const selectedItem = flow.inbox.data?.items.find(item => item.capture_id === captureId);
  const availableCaptureIds = useRef(new Set<string>());
  availableCaptureIds.current = new Set(flow.inbox.data?.items.map(item => item.capture_id) ?? []);
  useEffect(() => {
    lifetime.current = true;
    return () => {
      lifetime.current = false;
    };
  }, []);
  const recordReceipt = (request: ApprovalOperation, result: unknown) => {
    if (!lifetime.current || !isCurrentDraftContext(owner)) return;
    const accepted = validatedReceipt(request, result);
    clearReviewCarryForOrigin(owner, request);
    pendingApprovals.current.delete(approvalOperationKey(request));
    setVisited(previous => previous.filter(id => id !== request.captureId));
    if (sameApprovalOperation(pendingApproval.current, request)) {
      pendingApproval.current = null;
      setReconciliationError((current) =>
        current?.operationKey === approvalOperationKey(request) ? null : current,
      );
    }
    if (accepted?.schema_version === "coaching-private-feedback-accepted.v1") {
      approvalFailures.delete(approvalOperationKey(request));
      setPrivateReceipt(accepted);
      setPrivateResultOpen(visibleReview.current.active && visibleReview.current.captureId === request.captureId);
    } else if (accepted?.schema_version === 'coaching-feedback-accepted.v1') {
      approvalFailures.delete(approvalOperationKey(request)); setPublicReceipt(accepted);
      setPublicResultOpen(visibleReview.current.active && visibleReview.current.captureId === request.captureId);
    } else if (accepted?.schema_version === 'coaching-goal-accepted.v1') {
      approvalFailures.delete(approvalOperationKey(request));
      setPublishedReceipt(`Created player goal #${accepted.goal_id}. This is the original acceptance receipt.`);
    } else {
      const receipt = result as import("@/lib/types").CoachingApprovalResponse;
      setPublishedReceipt(`Published ${receipt.artifact_kind} #${receipt.artifact_id}`);
    }
    setCaptureId((current) => (current === request.captureId ? null : current));
  };
  const observeApproval = (
    request: ApprovalOperation,
    work: () => Promise<unknown>,
  ) => {
    const measurement = effort.session;
    const key = approvalOperationKey(request);
    if (isTaggedOperation(request)) {
      const outstanding = approvalInFlight.get(key) ?? 0;
      // A route return can retry before the first response finishes. A refusal
      // of that retry cannot establish whether the earlier dispatch committed.
      if (outstanding > 0) approvalFailures.set(key, {
        disposition: 'retain_original', reason: 'unknown', mayHaveAccepted: true,
      });
      approvalInFlight.set(key, outstanding + 1);
    }
    const operation = (async () => {
      try {
        await measurement?.beforeSave({ id: request.draftId, revision: request.body.expected_draft_revision }).catch(() => undefined);
        if (!lifetime.current || !isCurrentDraftContext(owner)) throw new Error("Coaching context changed");
        const result = await work();
        validatedReceipt(request, result);
        return result;
      } finally {
        if (isTaggedOperation(request)) {
          const remaining = (approvalInFlight.get(key) ?? 1) - 1;
          if (remaining) approvalInFlight.set(key, remaining); else approvalInFlight.delete(key);
        }
      }
    })();
    pendingApproval.current = structuredClone(request);
    pendingApprovals.current.set(approvalOperationKey(request), structuredClone(request));
    void operation.then(
      (result) => {
        if (lifetime.current && isCurrentDraftContext(owner)) void measurement?.received({ event: "outcome", outcome: "approved", domain_operation_id: request.body.client_operation_id, edit_categories: request.metricLabels ?? null });
        if (isTaggedOperation(request) || !availableCaptureIds.current.has(request.captureId)) recordReceipt(request, result);
      },
      (error) => {
        if (isTaggedOperation(request) && isCurrentDraftContext(owner)) {
          const key = approvalOperationKey(request);
          approvalFailures.set(key, classifyPrivateApprovalFailure(error, {
            mayHaveAccepted: approvalFailures.get(key)?.mayHaveAccepted ?? false,
          }));
        }
        measurement?.domainFailed();
        if (
          lifetime.current &&
          isCurrentDraftContext(owner) &&
          sameApprovalOperation(pendingApproval.current, request)
        )
          setReconciliationError({
            operationKey: approvalOperationKey(request),
            message: errorMessage(error),
          });
      },
    );
    return operation;
  };
  const reconcileApproval = async (request: ApprovalOperation) => {
    if (
      reconcilingOperationKey === approvalOperationKey(request) ||
      !isCurrentDraftContext(owner)
    )
      return;
    const operationKey = approvalOperationKey(request);
    setReconcilingOperationKey(operationKey);
    setReconciliationError((current) =>
      current?.operationKey === operationKey ? null : current,
    );
    try {
      const result = await flow.mutation.mutateAsync(() =>
        observeApproval(request, () => dispatchApproval(flow, request)),
      );
      recordReceipt(request, result);
    } catch (error) {
      if (
        lifetime.current &&
        isCurrentDraftContext(owner) &&
        pendingApprovals.current.has(approvalOperationKey(request))
      )
        setReconciliationError({ operationKey, message: errorMessage(error) });
    } finally {
      if (lifetime.current && isCurrentDraftContext(owner))
        setReconcilingOperationKey((current) =>
          current === operationKey ? null : current,
        );
    }
  };
  useEffect(() => {
    if (captureId) setOpen(true);
  }, [captureId]);
  useEffect(() => {
    if (!requestedCaptureId) return;
    setCaptureId(requestedCaptureId);
    setReviewOpen(true);
    setOpen(true);
  }, [requestedCaptureId, selectionIntent]);
  useEffect(() => {
    if (captureId) setVisited(previous => previous.includes(captureId) ? previous : [...previous, captureId]);
  }, [captureId]);
  const items = flow.inbox.data?.items ?? [];
  return (
    <section
      className={embedded ? "min-w-0" : "min-w-0 rounded-panel border border-rule bg-surface p-3"}
      aria-label="Private draft inbox"
    >
      <div className="flex flex-wrap items-center gap-2">
        {!embedded && <Button
          variant="ghost"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
        >
          Private draft inbox ({items.length})
        </Button>}
        <span className="text-xs text-ink-muted">
          Only your unfinished captures. Private approval saves only for you.
        </span>
      </div>
      {publishedReceipt && <p role="status">{publishedReceipt}</p>}
      {privateReceipt && <>
        <p role="status">Saved privately. <Button variant="ghost" onClick={() => setPrivateResultOpen(true)}>View saved Feedback</Button></p>
        <CoachModal title="Saved privately" description="Only me" active={visible && privateResultOpen} onClose={() => setPrivateResultOpen(false)}>
          <PrivateSavedFeedback owner={owner} accepted={privateReceipt} active={visible && privateResultOpen} onOpen={onOpenSavedFeedback} />
        </CoachModal>
      </>}
      {publicReceipt && <>
        <p role="status">Feedback published at version {publicReceipt.version}. <Button variant="ghost" onClick={() => setPublicResultOpen(true)}>View published Feedback</Button></p>
        <CoachModal title="Feedback published" active={visible && publicResultOpen} onClose={() => setPublicResultOpen(false)}>
          <PublishedFeedback owner={owner} accepted={publicReceipt} active={visible && publicResultOpen} onOpen={onOpenPublishedFeedback} />
        </CoachModal>
      </>}
      {effort.session && !selectedItem && <AuthoringEffortControls effort={effort} input={() => ({ task_kind: effort.session!.start.task_kind, mode: "assisted", player_ids: [] })} />}
      {open && (
        <div className="mt-3 grid min-w-0 gap-3">
          {flow.inbox.isLoading && (
            <p role="status">Loading private captures…</p>
          )}
          {flow.inbox.error && (
            <p role="alert">
              Inbox unavailable.{" "}
              <Button variant="ghost" onClick={() => void flow.inbox.refetch()}>
                Retry
              </Button>
            </p>
          )}
          {!flow.inbox.isLoading && !flow.inbox.error && !items.length && (
            <p className="text-sm text-ink-muted">No unfinished captures.</p>
          )}
          <div className="w-full min-w-0 divide-y divide-rule border-y border-rule">
            {items.map((item) => (
              <Button
                key={item.capture_id}
                data-testid={`draft-inbox-capture-${item.capture_id}`}
                variant="ghost"
                className="draft-inbox-row h-auto min-h-11 w-full min-w-0 justify-between whitespace-normal break-words rounded-none border-0 px-0 py-4 leading-normal text-left"
                onClick={() => { if (captureId !== item.capture_id) effort.reset(); setCaptureId(item.capture_id); setReviewOpen(true); }}
              >
                <span className="min-w-0">
                {item.kind === "marker" ? "Silent marker" : "Saved observation"}{" "}
                <span className="block text-xs font-normal text-ink-muted">{new Date(item.received_at).toLocaleString()} · {captureStatus(item.status)}</span>
                </span><ChevronRight aria-hidden="true" className="ml-3 size-4 shrink-0" />
              </Button>
            ))}
          </div>
          {visited.map(id => <RetainedCaptureReview
              key={`${id}:${owner.generation}`}
              owner={owner}
              captureId={id}
              active={visible && reviewOpen && id === captureId}
              players={roster.data?.players ?? []}
              onClose={() => {
                setReviewOpen(false);
              }}
              observeApproval={observeApproval}
              approvalFailure={request => approvalFailures.get(approvalOperationKey(request))}
              releaseApprovalRejection={request => {
                const key = approvalOperationKey(request);
                if (approvalFailures.get(key)?.disposition !== 'fresh_rejection') return false;
                approvalFailures.delete(key);
                pendingApprovals.current.delete(key);
                if (sameApprovalOperation(pendingApproval.current, request)) pendingApproval.current = null;
                return true;
              }}
              effort={effort}
              onManualFeedback={onManualFeedback}
              onPublished={(message, request) => {
                pendingApprovals.current.delete(approvalOperationKey(request));
                setVisited(previous => previous.filter(id => id !== request.captureId));
                if (sameApprovalOperation(pendingApproval.current, request))
                  pendingApproval.current = null;
                setReconciliationError((current) =>
                  current?.operationKey === approvalOperationKey(request)
                    ? null
                    : current,
                );
                setPublishedReceipt(message);
                setCaptureId((current) =>
                  current === request.captureId ? null : current,
                );
              }}
            />)}
          {Array.from(pendingApprovals.current.values()).filter(request => !items.some(item => item.capture_id === request.captureId)).map(request => (
            <div key={approvalOperationKey(request)} className="grid gap-2 border-t border-rule pt-3" role="status" data-testid={`draft-inbox-reconcile-${request.captureId}`}>
              <p>
                {isPrivateOperation(request) ? 'Private save' : 'Publication'} acknowledgment is unresolved. Current source and
                proposal are unavailable.
              </p>
              {reconciliationError?.operationKey ===
                approvalOperationKey(request) && (
                <p role="alert">{reconciliationError.message}</p>
              )}
              <Button
                disabled={
                  reconcilingOperationKey ===
                  approvalOperationKey(request)
                }
                onClick={() => void reconcileApproval(request)}
              >
                Retry unchanged operation
              </Button>
            </div>
          ))}
          {captureId &&
            !selectedItem &&
            !pendingApproval.current &&
            !flow.inbox.isLoading && (
              <p role="status">
                This capture is no longer available in your inbox.{" "}
                <Button
                  variant="ghost"
                  onClick={() => {
                    setCaptureId(null);
                    setReviewOpen(false);
                  }}
                >
                  Close
                </Button>
              </p>
            )}
          {(!reviewOpen || !selectedItem) && <Button variant="ghost" onClick={onManualFeedback}>
            Write feedback manually
          </Button>}
        </div>
      )}
    </section>
  );
}
function captureStatus(status: import('@/lib/types').CoachingCapture['status']): string {
  return { received: 'Private capture · confirm facts and recipients', queued: 'AI draft queued', processing: 'AI drafting in progress', needs_review: 'Private AI draft · review before approval', approved: 'Approved publication', discarded: 'Capture discarded', blocked: 'Processing blocked · manual feedback available', retryable: 'Request needs attention · review before retrying' }[status];
}
function RetainedCaptureReview({ captureId, active, ...props }: Omit<React.ComponentProps<typeof CaptureReview>, 'flow'> & { captureId: string; active: boolean }) {
  const flow = useDraftInbox(props.owner, captureId, active);
  if (!flow.item) return null;
  return <CaptureReview {...props} flow={flow} active={active} />;
}
function CaptureReview({
  active,
  owner,
  flow,
  players,
  onClose,
  onPublished,
  observeApproval,
  approvalFailure,
  releaseApprovalRejection,
  effort,
  onManualFeedback,
}: {
  active: boolean;
  owner: DraftContext;
  flow: ReturnType<typeof useDraftInbox>;
  players: import("@/lib/types").Player[];
  onClose: () => void;
  onPublished: (message: string, request: ApprovalOperation) => void;
  observeApproval: (
    request: ApprovalOperation,
    work: () => Promise<unknown>,
  ) => Promise<unknown>;
  approvalFailure: (request: ApprovalOperation) => PrivateApprovalFailure | undefined;
  releaseApprovalRejection: (request: ApprovalOperation) => boolean;
  effort: AuthoringEffort;
  onManualFeedback: () => void;
}) {
  const item = flow.item!;
  const [portal, setPortal] = useState<HTMLDivElement | null>(null);
  const namespace = `inbox:${item.capture_id}`;
  const [selection, setSelection] = useOwnedAssistState<CoachingPublicationSelection>(owner, `${namespace}:selection`, {
    kind: "feedback",
    publication_class: "player",
    player_ids: [],
  });
  const [preview, setPreview] = useState<CoachingPublicationPreview | null>(
    null,
  );
  const [editingConfirmation, setEditingConfirmation] = useOwnedAssistState(owner, `${namespace}:editing-confirmation`, false);
  const currentConfirmation =
    item.source_revision &&
    !item.confirmation_code &&
    !flow.confirmation.error &&
    item.confirmation &&
    flow.confirmation.data?.id === item.confirmation.id &&
    flow.confirmation.data.revision === item.confirmation.revision
      ? flow.confirmation.data
      : null;
  const confirmation = editingConfirmation ? null : currentConfirmation;
  const [reviewed, setReviewed] = useState(false);
  useEffect(() => {
    if (!active) {
      setPreview(null);
      setReviewed(false);
    }
  }, [active, currentConfirmation?.selection.kind, currentConfirmation?.selection.publication_class]);
  const [factDraft, setFactDraft] = useOwnedAssistState<ConfirmationForm>(owner, `${namespace}:fact`, {
    statement: '', basis: 'reported', relationship: 'unknown', observerId: null, precision: 'unknown', occurrence: '',
  });
  const factForm = useForm<ConfirmationForm>({
    resolver: zodResolver(confirmationFactSchema),
    defaultValues: factDraft,
  });
  const statement = factForm.watch("statement"),
    basis = factForm.watch("basis"),
    relationship = factForm.watch("relationship"),
    date = factForm.watch("occurrence"),
    precision = factForm.watch("precision"),
    observerId = factForm.watch("observerId");
  useEffect(() => {
    setFactDraft({ statement, basis, relationship, occurrence: date, precision, observerId });
  }, [statement, basis, relationship, date, precision, observerId]);
  const setStatement = (value: string) =>
    factForm.setValue("statement", value, { shouldDirty: true });
  const setBasis = (value: ConfirmationForm["basis"]) =>
    factForm.setValue("basis", value, { shouldDirty: true });
  const setRelationship = (value: ConfirmationForm["relationship"]) =>
    factForm.setValue("relationship", value, { shouldDirty: true });
  const setObserverId = (value: string | null) =>
    factForm.setValue("observerId", value, { shouldDirty: true });
  const setPrecision = (value: ConfirmationForm["precision"]) =>
    factForm.setValue("precision", value, { shouldDirty: true });
  const setDate = (value: string) =>
    factForm.setValue("occurrence", value, { shouldDirty: true });
  const me = useAuthStore((s) => s.me);
  const selfRoles =
    me?.memberships
      .filter(
        (m) =>
          m.team_id === owner.teamId &&
          ["coach", "assistant_coach"].includes(m.role),
      )
      .map((m) => m.role) ?? [];
  const observers = [
    {
      value: "unknown",
      label: "Observer unknown",
      id: null,
      relationship: "unknown" as const,
    },
    ...selfRoles.map((role) => ({
      value: `${owner.actorId}:${role}`,
      label: `${me?.user.display_name || "Me"} · ${role.replace("_", " ")}`,
      id: owner.actorId,
      relationship: role as "coach" | "assistant_coach",
    })),
    ...players
      .filter((p) => selection.player_ids.includes(p.id))
      .flatMap((p) =>
        p.links
          .filter(
            (link) =>
              link.relationship === "guardian" || link.relationship === "self",
          )
          .map((link) => ({
            value: `${link.user_id}:${link.relationship}`,
            label: `${link.display_name || "Name unavailable"} · ${link.relationship}`,
            id: link.user_id,
            relationship: link.relationship as "guardian" | "self",
          })),
      ),
  ];
  const validObservers = observers.filter(
    (o, i, list) =>
      list.findIndex((x) => x.value === o.value) === i &&
      (basis === "reported" ||
        ["coach", "assistant_coach"].includes(o.relationship)),
  );
  const [admittedJob, setAdmittedJob] = useOwnedAssistState<CoachingAIJobProgress | null>(owner, `${namespace}:job`, null);
  const currentJob = item.job ?? admittedJob;
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false);
  const [receipt, setReceipt] = useState<string | null>(null);
  const [confirmationOp] = useOwnedAssistState(owner, `${namespace}:confirm-operation`, { current: new FrozenOperation<CreateCoachingConfirmationRequest>() });
  const [generationOp] = useOwnedAssistState(owner, `${namespace}:generate-operation`, { current: new FrozenOperation<GenerationOperation>() });
  const [generationHistory, setGenerationHistory] = useOwnedAssistState<GenerationHistory | null>(owner, `${namespace}:generate-history`, null);
  const [approvalOp] = useOwnedAssistState(owner, `${namespace}:approve-operation`, { current: new FrozenOperation<ApprovalOperation>() });
  const [discardOp] = useOwnedAssistState(owner, `${namespace}:discard-operation`, { current: new FrozenOperation<DiscardCoachingCaptureRequest>() });
  const [awaiting, setAwaiting] = useOwnedAssistState<
    "confirmation" | "generate" | "approve" | "discard" | null
  >(owner, `${namespace}:awaiting`, null);
  const alive = useRef(true);
  const currentSource = useRef({
    id: item.capture_id,
    revision: item.source_revision,
  });
  currentSource.current = {
    id: item.capture_id,
    revision: item.source_revision,
  };
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  useEffect(() => {
    setPreview(null);
    setReviewed(false);
    setAdmittedJob(null);
  }, [item.source_revision]);
  const observeGeneration = async (request: GenerationOperation, work: () => Promise<CoachingAIJobProgress>) => {
    const operationId = request.body.client_operation_id;
    setGenerationHistory(previous => {
      const current = previous?.operationId === operationId ? previous : null;
      return { operationId, inFlight: (current?.inFlight ?? 0) + 1,
        mayHaveAdmitted: !!current?.mayHaveAdmitted || (current?.inFlight ?? 0) > 0, rejection: null };
    });
    try {
      const result = await work();
      setGenerationHistory(current => current?.operationId === operationId ? { ...current, mayHaveAdmitted: true } : current);
      return result;
    } catch (error) {
      const rejection = generationRejection(error);
      // Owned state is updated even after this component unmounts. Only a
      // current owner can retain it; a later remount must not lose uncertainty.
      setGenerationHistory(current => current?.operationId === operationId
        ? { ...current, mayHaveAdmitted: current.mayHaveAdmitted || !rejection, rejection } : current);
      throw error;
    } finally {
      setGenerationHistory(current => current?.operationId === operationId ? { ...current, inFlight: current.inFlight - 1 } : current);
    }
  };
  const settleGeneration = (result: unknown) => {
    setAdmittedJob(result as CoachingAIJobProgress);
    generationOp.current.reset();
    setGenerationHistory(null);
  };
  const attempt = async (
    kind: typeof awaiting,
    work: () => Promise<unknown>,
    settle?: (result: unknown) => void,
  ) => {
    if (inFlight.current || !isCurrentDraftContext(owner)) return;
    const dispatchedSource = { ...currentSource.current };
    inFlight.current = true;
    setAwaiting(kind);
    setBusy(true);
    setError("");
    try {
      const result = await flow.mutation.mutateAsync(work);
      if (alive.current && isCurrentDraftContext(owner)) {
        if (
          currentSource.current.id !== dispatchedSource.id ||
          currentSource.current.revision !== dispatchedSource.revision
        ) {
          setError(
            "Source changed while this request was pending. Refresh the current review.",
          );
          setAwaiting(kind);
          setPreview(null);
          setReviewed(false);
          return;
        }
        setAwaiting(null);
        settle?.(result);
      }
    } catch (e) {
      if (alive.current && isCurrentDraftContext(owner)) {
        setError(errorMessage(e));
        setAwaiting(kind);
        setPreview(null);
        setReviewed(false);
        void flow.source.refetch();
        if (item.confirmation) void flow.confirmation.refetch();
        if (item.draft) void flow.draft.refetch();
      }
    } finally {
      inFlight.current = false;
      if (alive.current && isCurrentDraftContext(owner)) setBusy(false);
    }
  };
  const sourcePermitted =
    !!item.source_revision &&
    flow.source.data?.capture.revision === item.revision &&
    !!flow.source.data &&
    !flow.source.error &&
    !flow.inbox.error;
  useEffect(() => {
    if (
      !item.source_revision ||
      [
        flow.source.error,
        flow.inbox.error,
        flow.confirmation.error,
        flow.draft.error,
      ].some((e) => e instanceof ApiError && [401, 403, 404].includes(e.status))
    )
      clearReviewCarryForCapture(owner, item.capture_id);
  }, [
    flow.source.error,
    flow.inbox.error,
    item.source_revision,
    flow.confirmation.error,
    flow.draft.error,
    owner,
  ]);
  const previewAudience = preview ?? confirmation;
  const audienceOK =
    !!previewAudience &&
    (!preview || preview.capture_source_revision === item.source_revision) &&
    previewAudience.selection.player_ids.every((id) =>
      players.some((p) => p.id === id),
    ) &&
    recipientsReviewable(
      previewAudience.recipients,
      previewAudience.recipient_user_ids,
    );
  useEffect(() => {
    setPreview(null);
    setReviewed(false);
  }, [currentConfirmation?.preview_revision]);
  const refreshAudience = () =>
    void attempt(
      null,
      () => flow.preview(item.capture_id, confirmation?.selection ?? selection),
      (result) => {
        setPreview(result as CoachingPublicationPreview);
        setReviewed(false);
      },
    );
  const confirm = () => {
    if (
      !preview ||
      !reviewed ||
      !audienceOK ||
      !statement.trim() ||
      !sourcePermitted
    )
      return;
    const request = confirmationOp.current.begin(() => ({
      client_operation_id: crypto.randomUUID(),
      expected_capture_revision: preview.capture_revision,
      expected_confirmation_revision: preview.confirmation_revision,
      preview_revision: preview.preview_revision,
      recipient_user_ids: preview.recipient_user_ids,
      selection: preview.selection,
      fact: humanConfirmationFact(factForm.getValues()),
    }));
    void attempt(
      "confirmation",
      () => flow.confirm(item.capture_id, request),
      () => {
        setEditingConfirmation(false);
        confirmationOp.current.reset();
      },
    );
  };
  const generate = () => {
    if (!confirmation || !sourcePermitted) return;
    const request = generationOp.current.begin(() => ({
      id: confirmation.id,
      body: {
        client_operation_id: crypto.randomUUID(),
        expected_confirmation_revision: confirmation.revision,
      },
    }));
    void attempt(
      "generate",
      () => observeGeneration(request, () => flow.generate(request.id, request.body)),
      settleGeneration,
    );
  };
  const [metricLabels, setMetricLabels] = useState<EditCategory[] | null>(null);
  const discard = () => {
    const request = discardOp.current.begin(() => ({
      client_operation_id: crypto.randomUUID(),
      expected_revision: item.revision,
      status: "discarded",
    }));
    void attempt(
      "discard",
      async () => {
        const measurement = effort.session;
        if (flow.draft.data?.draft.schema_version === "coaching-draft.v4") await measurement?.beforeSave({ id: flow.draft.data.draft.id, revision: flow.draft.data.draft.revision }).catch(() => undefined);
        if (!isCurrentDraftContext(owner)) throw new Error("Coaching context changed");
        let result: unknown;
        try { result = await flow.discard(item.capture_id, request); } catch (error) { measurement?.domainFailed(); throw error; }
        void measurement?.received({ event: "outcome", outcome: "discarded", domain_operation_id: request.client_operation_id });
        return result;
      },
      () => {
        clearReviewCarryForCapture(owner, item.capture_id);
        clearOwnedAssistState(owner, namespace);
        onClose();
      },
    );
  };
  const draft =
    flow.draft.error || item.draft_code || !sourcePermitted || !confirmation
      ? undefined
      : flow.draft.data?.draft;
  const published = (request: ApprovalOperation, result: unknown) => {
    const r = result as import("@/lib/types").CoachingApprovalResponse;
    clearReviewCarryForOrigin(owner, request);
    clearOwnedAssistState(owner, namespace);
    setReceipt(`Published ${r.artifact_kind} #${r.artifact_id}`);
    onPublished(`Published ${r.artifact_kind} #${r.artifact_id}`, request);
    approvalOp.current.reset();
  };
  const approveGoal = (content: CanonicalCoachingGoalContent) => {
    if (!active || busy || awaiting || !draft || !confirmation || !preview || !reviewed || !audienceOK || !sourcePermitted
        || preview.selection.kind !== 'action' || preview.selection.player_ids.length !== 1) return;
    const request = approvalOp.current.begin(() => ({
      captureId: item.capture_id, draftId: draft.id, metricLabels: metricLabels === null ? null : [...metricLabels],
      body: { schema_version: 'coaching-goal-approval.v1', client_operation_id: crypto.randomUUID(),
        expected_draft_revision: draft.revision, confirmation_id: confirmation.id,
        expected_confirmation_revision: confirmation.revision, preview_revision: preview.preview_revision,
        recipient_user_ids: [...preview.recipient_user_ids], selection: structuredClone(preview.selection), content: structuredClone(content) },
    }));
    void attempt('approve', () => observeApproval(request, () => dispatchApproval(flow, request)));
  };
  const approvePublic = (writing: CanonicalFeedbackReviewCarry) => {
    if (!active || busy || awaiting || !draft || !confirmation || !preview || !reviewed || !audienceOK || !sourcePermitted
        || preview.selection.kind !== 'feedback' || preview.selection.publication_class === 'creator_private') return;
    const exact = decodeCanonicalFeedbackReviewCarry(writing);
    if (!exact || !feedbackProseLength(exact.content.message)
        || !isFeedbackProseWithinLimit('message', exact.content.message)
        || !isFeedbackProseWithinLimit('next_step', exact.content.next_step)
        || exact.recording_entry_id && !exact.recording) return;
    const request = approvalOp.current.begin(() => ({
      captureId: item.capture_id, draftId: draft.id, metricLabels: metricLabels === null ? null : [...metricLabels],
      body: { schema_version: 'coaching-feedback-approval.v1', client_operation_id: crypto.randomUUID(),
        expected_draft_revision: draft.revision, confirmation_id: confirmation.id,
        expected_confirmation_revision: confirmation.revision, preview_revision: preview.preview_revision,
        recipient_user_ids: [...preview.recipient_user_ids], selection: structuredClone(preview.selection),
        content: structuredClone(exact.content), ...(exact.recording ?? {}) },
    }));
    void attempt('approve', () => observeApproval(request, () => dispatchApproval(flow, request)));
  };
  const approvePrivate = (writing: PrivateFeedbackReviewCarry) => {
    if (!active || busy || awaiting || !draft || !confirmation || !preview || !reviewed || !audienceOK || !sourcePermitted
        || preview.selection.kind !== 'feedback' || preview.selection.publication_class !== 'creator_private') return;
    const exact = decodePrivateFeedbackReviewCarry(writing);
    if (!exact || !feedbackProseLength(exact.content.message)
        || !isFeedbackProseWithinLimit('message', exact.content.message)
        || !isFeedbackProseWithinLimit('next_step', exact.content.next_step)
        || exact.recording_entry_id && !exact.recording) return;
    const request = approvalOp.current.begin(() => ({
      captureId: item.capture_id, draftId: draft.id,
      metricLabels: metricLabels === null ? null : [...metricLabels],
      body: {
        schema_version: 'coaching-private-feedback-approval.v1',
        client_operation_id: crypto.randomUUID(),
        expected_draft_revision: draft.revision,
        confirmation_id: confirmation.id,
        expected_confirmation_revision: confirmation.revision,
        preview_revision: preview.preview_revision,
        recipient_user_ids: [...preview.recipient_user_ids],
        selection: structuredClone(preview.selection),
        content: structuredClone(exact.content),
        ...(exact.recording ?? {}),
      },
    }));
    void attempt('approve', () => observeApproval(request, () => dispatchApproval(flow, request)));
  };
  const pendingApprovalRequest = awaiting === 'approve' && approvalOp.current.pending
    ? approvalOp.current.begin(() => { throw new Error('Missing original approval'); }) : null;
  const taggedPending = pendingApprovalRequest && isTaggedOperation(pendingApprovalRequest) ? pendingApprovalRequest : null;
  const generationCorrectable = awaiting === 'generate' && !!generationHistory?.rejection
    && !generationHistory.mayHaveAdmitted && generationHistory.inFlight === 0;
  const generationDiagnostic = awaiting === 'generate' && generationHistory?.rejection
    ? generationHistory.rejection === 'occupancy_unavailable'
      ? 'No AI drafting capacity is available (occupancy_unavailable). Your confirmed facts and writing are kept. You can write feedback manually.'
      : generationHistory.rejection === 'confirmation_revision_conflict' || generationHistory.rejection === 'publication_preview_stale'
      ? `Confirmation changed (${generationHistory.rejection}). Review the current facts and recipients before requesting another draft.`
      : `AI drafting is temporarily unavailable (${generationHistory.rejection}). Your confirmed facts and writing are kept.` : null;
  // The heading follows the same permitted review state as the form, including
  // a return to fact editing. Cached query objects alone do not establish a step.
  const reviewStep = receipt
    ? "Publication complete"
    : !sourcePermitted
      ? "Review paused · current source unavailable"
      : !confirmation
        ? "1. Check facts and recipients"
        : draft?.schema_version === "coaching-draft.v4" && draft.status === "needs_review"
          ? "3. Review and approve"
          : "2. Draft with AI";
  return (
    <CoachModal title="Review saved observation" size="wide" active={active} onClose={onClose} description={reviewStep}>
    {receipt ? <p role="status">
      {receipt}
      <Button variant="ghost" onClick={onClose}>Close</Button>
    </p> : <div
      ref={setPortal}
      onChangeCapture={() => queueMicrotask(() => setFactDraft(factForm.getValues()))}
      onClickCapture={event => {
        if ((busy || awaiting) && event.target instanceof Element && event.target.closest('a[href]')) {
          event.preventDefault(); event.stopPropagation();
        }
      }}
      className="grid min-w-0 grid-cols-1 gap-3 break-words border-t border-rule pt-3"
      data-testid="draft-inbox-review"
    >
      <AuthoringEffortControls effort={effort} labels={metricLabels} onLabels={setMetricLabels} input={() => ({ task_kind: (confirmation?.selection ?? selection).kind === "action" ? "goal" : "feedback", mode: "assisted", player_ids: (confirmation?.selection ?? selection).player_ids, origin: { kind: "capture", source_id: item.capture_id, source_revision: item.revision } })} />
      <div className="flex min-w-0 flex-wrap gap-2">
        <Button variant="ghost" disabled={busy || !!awaiting || ['queued', 'processing'].includes(item.status)} onClick={discard}>
          Discard private capture
        </Button>
        <Button variant="outline" onClick={onManualFeedback}>Write feedback manually</Button>
      </div>
      {['queued', 'processing'].includes(item.status) && <p className="text-sm text-ink-muted">AI drafting is in progress. Nothing is published; discard becomes available when processing finishes.</p>}
      {flow.inbox.error && <p role="alert" className="text-sm">Current draft status could not be refreshed. Your work is retained.
        <Button type="button" variant="outline" disabled={flow.inbox.isFetching} onClick={() => void flow.inbox.refetch()}>Check draft status</Button>
      </p>}
      {flow.source.isLoading && <p role="status">Loading permitted source…</p>}
      {!sourcePermitted && (
        <p role="alert">
          Source unavailable. Review and publication are paused.
          <Button type="button" variant="ghost" onClick={() => void flow.source.refetch()}>Reload source</Button>
        </p>
      )}
      {sourcePermitted && (
        <section>
          <h4 className="text-sm font-display">Original private capture</h4>
          <p className="whitespace-pre-wrap text-sm">
            {flow.source.data!.capture.text ??
              "Silent marker: supply your own factual statement."}
          </p>
          <p className="text-xs text-ink-muted">
            Recorded {new Date(item.occurred_at).toLocaleString()} · Received{" "}
            {new Date(item.received_at).toLocaleString()} · Expires{" "}
            {new Date(item.expires_at).toLocaleString()}. Capture time does not
            establish fact occurrence. Player hints remain unresolved.
          </p>
        </section>
      )}
      {(generationDiagnostic || error) && <p role="alert">{generationDiagnostic || error}</p>}
      {awaiting && (
        <div className="flex flex-wrap gap-2">
          <p className="text-sm">
            {generationCorrectable ? 'This request was not admitted. You can review your confirmation before trying again.'
              : 'Acknowledgment is unresolved. Retry the exact frozen operation.'}
          </p>
          <Button
            disabled={busy}
            onClick={() => {
              const op = awaiting;
              if (op === "confirmation") {
                const r = confirmationOp.current.begin(() => {
                  throw new Error("Missing request");
                });
                void attempt(
                  op,
                  () => flow.confirm(item.capture_id, r),
                  () => {
                    setEditingConfirmation(false);
                    confirmationOp.current.reset();
                  },
                );
              } else if (op === "generate") {
                const r = generationOp.current.begin(() => {
                  throw new Error("Missing request");
                });
                void attempt(
                  op,
                  () => observeGeneration(r, () => flow.generate(r.id, r.body)),
                  settleGeneration,
                );
              } else if (op === "discard") discard();
              else {
                const r = approvalOp.current.begin(() => {
                  throw new Error("Missing request");
                });
                const request: ApprovalOperation = r;
                void attempt(
                  op,
                  () => observeApproval(request, () => dispatchApproval(flow, request)),
                  (result) => { if (!isTaggedOperation(request)) published(request, result); },
                );
              }
            }}
          >
            Retry unchanged operation
          </Button>
          {(awaiting === 'generate' ? generationCorrectable : !taggedPending || approvalFailure(taggedPending)?.disposition === 'fresh_rejection') && <Button
            variant="outline"
            disabled={busy}
            onClick={() => {
              if (taggedPending && !releaseApprovalRejection(taggedPending)) return;
              confirmationOp.current.reset();
              generationOp.current.reset();
              setGenerationHistory(null);
              approvalOp.current.reset();
              discardOp.current.reset();
              setAwaiting(null);
              setReviewed(false);
            }}
          >
            {awaiting === 'generate' ? 'Review confirmation and try again' : taggedPending ? 'Correct writing and review again' : 'Begin a new review operation'}
          </Button>}
        </div>
      )}
      {!confirmation && sourcePermitted && (
        <fieldset disabled={busy || !!awaiting} className="grid gap-3">
          <TkticSelect
            portalContainer={portal}
            ariaLabel="Publication artifact"
            disabled={busy || !!awaiting}
            value={selection.kind}
            onChange={(kind) => {
              setSelection({
                kind: kind as "feedback" | "action",
                publication_class: "player",
                player_ids: [],
              });
              setPreview(null);
              setReviewed(false);
            }}
            options={[
              { value: "feedback", label: "Feedback" },
              { value: "action", label: "Single-player action" },
            ]}
          />
          <p className="text-sm">
            Resolve the real player{selection.kind === "feedback" ? "s" : ""}{" "}
            explicitly.
          </p>
          <ComposerPlayerPicker
            players={players}
            value={selection.player_ids}
            onChange={(player_ids) => {
              setSelection({
                ...selection,
                player_ids:
                  selection.kind === "action"
                    ? player_ids.slice(-1)
                    : player_ids,
              });
              setPreview(null);
              setReviewed(false);
            }}
          />
          <TkticSelect
            portalContainer={portal}
            ariaLabel="Publication audience"
            disabled={busy || !!awaiting}
            value={selection.publication_class}
            onChange={(publication_class) => {
              setSelection({
                ...selection,
                publication_class:
                  publication_class as CoachingPublicationSelection["publication_class"],
              });
              setPreview(null);
              setReviewed(false);
            }}
            options={[
              { value: "creator_private", label: selection.kind === "action" ? "Coaches" : "Only me" },
              {
                value: "player",
                label: "Selected players and their linked family",
              },
              ...(selection.kind === "feedback"
                ? [{ value: "team", label: "Team audience" }]
                : []),
            ]}
          />
          <label className="text-sm">
            Human factual statement (separate from proposed wording)
            <textarea
              value={statement}
              maxLength={990}
              onChange={(e) => setStatement(e.target.value)}
              className="mt-1 w-full rounded-control border border-rule bg-canvas p-2"
            />
          </label>
          <TkticSelect
            portalContainer={portal}
            ariaLabel="Factual basis"
            disabled={busy || !!awaiting}
            value={basis}
            onChange={(v) => {
              setBasis(v as typeof basis);
              setRelationship("unknown");
              setObserverId(null);
            }}
            options={[
              { value: "reported", label: "Reported to me" },
              { value: "observed", label: "Personally observed" },
            ]}
          />
          <TkticSelect
            portalContainer={portal}
            ariaLabel="Actual observer"
            disabled={busy || !!awaiting}
            value={
              relationship === "unknown"
                ? "unknown"
                : `${observerId}:${relationship}`
            }
            onChange={(value) => {
              const selected = validObservers.find((o) => o.value === value);
              if (selected) {
                setObserverId(selected.id);
                setRelationship(selected.relationship);
              }
            }}
            options={validObservers.map((o) => ({
              value: o.value,
              label: o.label,
            }))}
            placeholder="Select the actual observer"
          />
          <TkticSelect
            portalContainer={portal}
            ariaLabel="Occurrence precision"
            disabled={busy || !!awaiting}
            value={precision}
            onChange={(v) => {
              setPrecision(v as typeof precision);
              setDate("");
            }}
            options={[
              { value: "unknown", label: "Occurrence unknown" },
              { value: "day", label: "Known day" },
              { value: "instant", label: "Known instant with timezone" },
            ]}
          />
          {precision === "day" ? (
            <label className="text-sm">
              Actual occurrence day
              <TkticDateInput value={date} onChange={setDate} />
            </label>
          ) : precision === "instant" ? (
            <label className="text-sm">
              Actual occurrence instant with timezone
              <input
                value={date}
                onChange={(e) => setDate(e.target.value)}
                placeholder="2026-10-07T15:00:00Z"
                className="mt-1 w-full rounded-control border border-rule bg-canvas p-2"
              />
            </label>
          ) : (
            <p className="text-sm text-ink-muted">
              Occurrence stays unknown; capture time is not substituted.
            </p>
          )}
        </fieldset>
      )}
      {confirmation && sourcePermitted && (
        <section>
          <h4 className="font-display">Confirmed human fact</h4>
          <p className="whitespace-pre-wrap text-sm">
            {confirmation.fact.statement}
          </p>
          <p className="text-xs text-ink-muted">
            {confirmation.fact.basis} ·{" "}
            {confirmation.fact.observer_relationship} ·{" "}
            {confirmation.fact.occurrence_precision === "unknown"
              ? "Occurrence unknown"
              : confirmation.fact.occurred_at}{" "}
            · Reviewed by you. Final wording does not change this fact.
          </p>
        </section>
      )}
      {confirmation && (
        <Button
          variant="outline"
          disabled={busy || !!awaiting}
          onClick={() => {
            setSelection(confirmation.selection);
            setStatement(confirmation.fact.statement);
            setBasis(confirmation.fact.basis);
            setRelationship(
              confirmation.fact.observer_user_id === owner.actorId
                ? confirmation.fact.observer_relationship
                : "unknown",
            );
            setObserverId(confirmation.fact.observer_user_id ?? null);
            setRelationship(confirmation.fact.observer_relationship);
            setPrecision(confirmation.fact.occurrence_precision);
            setDate(confirmation.fact.occurred_at ?? "");
            setEditingConfirmation(true);
            setPreview(null);
            setReviewed(false);
          }}
        >
          Change confirmed evidence or selection
        </Button>
      )}
      <Button
        variant="outline"
        disabled={
          busy ||
          !!awaiting ||
          !sourcePermitted ||
          (!confirmation && !selection.player_ids.length)
        }
        onClick={refreshAudience}
      >
        Refresh complete recipient preview
      </Button>
      {previewAudience && sourcePermitted && (
        <section>
          <h4 className="font-display">Complete publication audience</h4>
          <p className="text-sm">
            {previewAudience.selection.kind === 'action' && previewAudience.selection.publication_class === 'creator_private' ? 'Coaches · not shared with the player' : publicationVisibility(previewAudience.selection.publication_class)} ·{" "}
            {previewAudience.selection.player_ids
              .map(
                (id) =>
                  players.find((p) => p.id === id)?.display_name ??
                  "Player name unavailable",
              )
              .join(", ")}
          </p>
          {previewAudience.recipients.length ? (
            <ul>
              {previewAudience.recipients.map((r) => (
                <li key={r.user_id} className="text-sm">
                  {r.label_status === "available" && r.display_name
                    ? r.display_name
                    : "Recipient name unavailable — review incomplete"}{" "}
                  · {r.relationships.join(", ")}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm">{previewAudience.selection.kind === "action" ? "Coaches. No player or family recipients." : "Only you. No player or family recipients."}</p>
          )}
          <Button
            variant={reviewed ? "primary" : "outline"}
            disabled={!preview || !audienceOK || busy || !!awaiting}
            onClick={() => setReviewed((v) => !v)}
            aria-pressed={reviewed}
          >
            {reviewed
              ? "Audience reviewed"
              : "I reviewed every actual recipient and resolved player"}
          </Button>
        </section>
      )}
      {!confirmation && <p className="text-xs text-ink-muted">Confirmation saves your facts and recipients. No AI call; nothing shared.</p>}
      {!confirmation && (
        <Button
          disabled={
            !reviewed ||
            !preview ||
            !statement.trim() ||
            busy ||
            !!awaiting ||
            !sourcePermitted ||
            !confirmationFactSchema.safeParse(factForm.getValues()).success ||
            (relationship !== "unknown" &&
              !validObservers.some(
                (o) => o.id === observerId && o.relationship === relationship,
              ))
          }
          onClick={confirm}
        >
          {busy && awaiting === "confirmation" ? "Confirming facts and recipients…" : "Confirm facts and recipients"}
        </Button>
      )}
      {confirmation && !draft && (
        <>
          <Button
            disabled={
              busy ||
              !!awaiting ||
              !sourcePermitted ||
              (!!currentJob &&
                (!currentJob.can_regenerate ||
                  ["queued", "processing"].includes(currentJob.status)))
            }
            onClick={generate}
          >
            {busy && awaiting === 'generate' || currentJob?.status === 'processing' ? `Drafting ${confirmation.selection.kind}…` : currentJob?.status === 'queued' ? 'AI draft queued…' : `Draft ${confirmation.selection.kind} with AI`}
          </Button>
          {currentJob && (
            <p role="status" className="text-sm">
              {draftJobMessage(currentJob, confirmation.selection.kind)}
            </p>
          )}
          <p className="text-xs text-ink-muted">AI drafts from your confirmed observation. Review the wording before sharing.</p>
        </>
      )}
      {(flow.confirmation.error ||
        flow.draft.error ||
        item.confirmation_code ||
        item.draft_code) && (
        <p role="alert">
          Current confirmation or proposal is stale or unavailable. Refresh
          current source and audience before continuing.
        </p>
      )}
      {draft && draft.schema_version !== "coaching-draft.v4" && <p role="status">This proposal uses an older evidence version. Review current confirmation before continuing; this proposal cannot be approved.</p>}
      {draft &&
        draft.schema_version === "coaching-draft.v4" &&
        draft.status === "needs_review" &&
        confirmation && (
          <ApprovalWriting
            key={`${draft.id}:${draft.revision}:${confirmation.revision}`}
            owner={owner}
            players={players}
            draft={draft}
            confirmation={confirmation}
            audience={preview}
            active={active}
            operationPending={busy || !!awaiting}
            disabled={
              busy || !!awaiting || !preview || !reviewed || !audienceOK
            }
            onApproveGoal={approveGoal}
            onApprovePublic={approvePublic}
            onApprovePrivate={approvePrivate}
          />
        )}
    </div>}
    </CoachModal>
  );
}
const goalSchema = z.object({
  title: z.string().trim().min(1).max(160),
  description: z.string().max(2000),
  successCriteria: z.string().max(2000),
});
type ApprovalWritingProps = {
  owner: DraftContext;
  players: import("@/lib/types").Player[];
  draft: CoachingApplicationDraft;
  confirmation: CoachingConfirmationResponse;
  audience: CoachingPublicationPreview | null;
  active: boolean;
  operationPending: boolean;
  disabled: boolean;
  onApproveGoal: (content: CanonicalCoachingGoalContent) => void;
  onApprovePublic: (writing: CanonicalFeedbackReviewCarry) => void;
  onApprovePrivate: (writing: PrivateFeedbackReviewCarry) => void;
};
function ApprovalWriting(props: ApprovalWritingProps) {
  return props.draft.kind === 'feedback' ? <FeedbackApprovalWriting {...props} /> : <GoalApprovalWriting {...props} />;
}
function GoalApprovalWriting({ owner, players, draft, confirmation, audience, operationPending, disabled, onApproveGoal }: ApprovalWritingProps) {
  const origin: ReviewOrigin = { captureId: draft.capture_id, draftId: draft.id, draftRevision: draft.revision,
    confirmationId: confirmation.id, confirmationRevision: confirmation.revision };
  const stored = useRef(readReviewCarry(owner, origin));
  const same = JSON.stringify(stored.current?.origin) === JSON.stringify(origin);
  const defaults = { ...proposedWriting(draft), ...(same ? stored.current?.edits : {}) };
  const [retainedApplied, setRetainedApplied] = useState(false);
  const [storageError, setStorageError] = useState('');
  const [review, setReview] = useState<CanonicalCoachingGoalContent | null>(null);
  const retainedEdits = useRef(stored.current?.edits ?? {});
  const goal = useForm<z.infer<typeof goalSchema>>({ resolver: zodResolver(goalSchema), defaultValues: {
    title: defaults.title, description: defaults.description ?? '', successCriteria: defaults.success_criteria ?? '',
  } });
  const persist = () => {
    const values = goal.getValues(), dirty = goal.formState.dirtyFields;
    const edits = boundedHumanEdits({ ...retainedEdits.current,
      ...(dirty.title ? { title: values.title } : {}), ...(dirty.description ? { description: values.description } : {}),
      ...(dirty.successCriteria ? { success_criteria: values.successCriteria } : {}),
    }, 'action');
    retainedEdits.current = edits;
    const saved = saveReviewCarry(owner, { kind: 'action', origin: !same && stored.current && !retainedApplied ? stored.current.origin : origin, edits });
    if (!saved) setStorageError('Your edits could not be retained. Resolve the original recording review before continuing.');
    return saved;
  };
  const persistRef = useRef(persist); persistRef.current = persist;
  useEffect(() => { const save = () => persistRef.current(); window.addEventListener('pagehide', save);
    return () => { save(); window.removeEventListener('pagehide', save); }; }, []);
  useEffect(() => { setReview(null); }, [audience]);
  const coachOnly = confirmation.selection.publication_class === 'creator_private';
  return <section className="grid min-w-0 gap-3 border-t border-rule pt-3" onChangeCapture={() => { setReview(null); queueMicrotask(() => persistRef.current()); }}>
    <h4 className="font-display">Proposed action — edit before approval</h4>
    <ProposalEvidence output={draft.structured_output} players={players} />
    {storageError && <p role="alert">{storageError}</p>}
    {!same && stored.current && !retainedApplied && <div><p>Your earlier human edits are retained separately from this revision.</p><Button variant="outline" disabled={operationPending} onClick={() => {
      const edits = stored.current!.edits;
      if (!saveReviewCarry(owner, { kind: 'action', origin, edits })) { setStorageError('The original review has retained Feedback or recording work. Resolve that review before continuing.'); return; }
      retainedEdits.current = edits; setRetainedApplied(true);
      goal.setValue('title', edits.title ?? defaults.title, { shouldDirty: true });
      goal.setValue('description', edits.description ?? defaults.description ?? '', { shouldDirty: true });
      goal.setValue('successCriteria', edits.success_criteria ?? defaults.success_criteria ?? '', { shouldDirty: true });
    }}>Apply my retained human edits to this revision</Button></div>}
    {!review ? <form className="grid gap-3" onSubmit={goal.handleSubmit(values => {
      if (disabled || confirmation.selection.player_ids.length !== 1 || !persist()) return;
      setReview({ player_id: confirmation.selection.player_ids[0], title: values.title, description: values.description,
        success_criteria: values.successCriteria, visibility: coachOnly ? 'coach' : 'player',
        priority: 'medium', target_date: '', coach_private_note: '', context: 'next_match', status: 'open' });
    })}>
      <GoalWritingFields title={goal.register('title')} description={goal.register('description')} successCriteria={goal.register('successCriteria')} errors={goal.formState.errors} />
      <p className="text-sm text-ink-muted">The confirmed fact and one player Goal are saved separately. Actions cannot attach video.</p>
      <Button disabled={disabled} type="submit">Preview and approve</Button>
    </form> : <section aria-label="Review and approve" className="grid gap-4">
      <h4 className="font-display">Review and approve</h4>
      <article className="rounded-panel bg-elevated p-4"><h5 className="font-display">{review.title}</h5><p className="whitespace-pre-wrap">{review.description}</p><p className="whitespace-pre-wrap">{review.success_criteria}</p></article>
      <h5 className="font-display">Who will receive this?</h5>
      <p>{coachOnly ? 'Coaches · not shared with the player' : 'Player visibility · selected player and permitted linked family'}</p>
      {!audience ? <p>Recipients are unavailable. Refresh the complete recipient preview before approval.</p> : <ul>{audience.recipients.map(recipient => <li key={recipient.user_id}>{recipient.display_name || 'Recipient name unavailable'} · {recipient.relationships.join(', ')}</li>)}</ul>}
      <p className="text-xs text-ink-muted">Approval creates the confirmed fact and one Goal. It does not mark the Goal achieved.</p>
      <div className="flex gap-2"><Button variant="outline" onClick={() => setReview(null)}>Back to writing</Button><Button disabled={disabled} onClick={() => onApproveGoal(review)}>Approve and publish once</Button></div>
    </section>}
  </section>;
}

function PrivateSavedFeedback({ owner, accepted, active, onOpen }: {
  owner: DraftContext; accepted: PrivateCoachingFeedbackAcceptedResponse; active: boolean;
  onOpen?: (request: SavedPrivateFeedbackRequest, signal: AbortSignal) => Promise<void>;
}) {
  const [error, setError] = useState('');
  const [opening, setOpening] = useState(false);
  const lifetime = useRef(0);
  const inFlight = useRef(false);
  const controller = useRef<AbortController | null>(null);
  useLayoutEffect(() => { lifetime.current += 1; inFlight.current = false; setOpening(false); setError('');
    return () => { lifetime.current += 1; controller.current?.abort(); controller.current = null; };
  }, [active, accepted]);
  const openSaved = async () => {
    if (!active || inFlight.current || !onOpen || !isCurrentDraftContext(owner)) return;
    const generation = lifetime.current;
    const attempt = new AbortController();
    controller.current = attempt;
    inFlight.current = true; setOpening(true); setError('');
    try { await onOpen({ owner: { ...owner }, accepted: structuredClone(accepted) }, attempt.signal); }
    catch (e) { if (lifetime.current === generation && isCurrentDraftContext(owner)) setError(errorMessage(e)); }
    finally { if (lifetime.current === generation && isCurrentDraftContext(owner)) { inFlight.current = false; controller.current = null; setOpening(false); } }
  };
  return <section className="grid gap-4" aria-label="Private Feedback receipt">
    <p>Your feedback was saved privately at version {accepted.version}. No player or family publication happened in this step.</p>
    <p className="text-sm text-ink-muted">This is the original save receipt. Open the saved Feedback to check its current state, edit it, or separately review publication.</p>
    {error && <p role="alert">{error}</p>}
    {onOpen && <Button disabled={!active || opening} onClick={() => void openSaved()}>{opening ? 'Opening saved Feedback…' : 'Open saved Feedback'}</Button>}
  </section>;
}

function PublishedFeedback({ owner, accepted, active, onOpen }: {
  owner: DraftContext; accepted: CoachingFeedbackAcceptedResponse; active: boolean;
  onOpen?: (request: SavedPublicFeedbackRequest, signal: AbortSignal) => Promise<void>;
}) {
  const [error, setError] = useState('');
  const [opening, setOpening] = useState(false);
  const lifetime = useRef(0);
  const inFlight = useRef(false);
  const controller = useRef<AbortController | null>(null);
  useLayoutEffect(() => { lifetime.current += 1; inFlight.current = false; setOpening(false); setError('');
    return () => { lifetime.current += 1; controller.current?.abort(); controller.current = null; };
  }, [active, accepted]);
  const openSaved = async () => {
    if (!active || inFlight.current || !onOpen || !isCurrentDraftContext(owner)) return;
    const generation = lifetime.current;
    const attempt = new AbortController();
    controller.current = attempt;
    inFlight.current = true; setOpening(true); setError('');
    try { await onOpen({ owner: { ...owner }, accepted: structuredClone(accepted) }, attempt.signal); }
    catch (e) { if (lifetime.current === generation && isCurrentDraftContext(owner)) setError(errorMessage(e)); }
    finally { if (lifetime.current === generation && isCurrentDraftContext(owner)) { inFlight.current = false; controller.current = null; setOpening(false); } }
  };
  return <section className="grid gap-4" aria-label="Published Feedback receipt">
    <p>Your feedback was published at version {accepted.version} for the reviewed audience.</p>
    <p className="text-sm text-ink-muted">This is the original publication receipt. Open the published Feedback to check its current state.</p>
    {error && <p role="alert">{error}</p>}
    {onOpen && <Button disabled={!active || opening} onClick={() => void openSaved()}>{opening ? 'Opening published Feedback…' : 'Open published Feedback'}</Button>}
  </section>;
}

function FeedbackApprovalWriting({ owner, players, draft, confirmation, audience, active, operationPending, disabled, onApprovePrivate, onApprovePublic }: ApprovalWritingProps) {
  const navigate = useNavigate();
  const privateMode = confirmation.selection.publication_class === 'creator_private';
  const overlay = privateMode ? 'privateFeedback' : 'canonicalFeedback';
  const target = { subject_mode: confirmation.selection.publication_class === 'team' ? 'team' as const : 'players' as const,
    player_ids: confirmation.selection.publication_class === 'team' ? [] : [...confirmation.selection.player_ids],
    audience: privateMode ? null : confirmation.selection.publication_class === 'team' ? 'team' as const : 'players' as const };
  const origin: ReviewOrigin = {
    captureId: draft.capture_id, draftId: draft.id, draftRevision: draft.revision,
    confirmationId: confirmation.id, confirmationRevision: confirmation.revision,
  };
  const originKey = JSON.stringify(origin);
  const stored = useRef(readReviewCarry(owner, { captureId: draft.capture_id, draftId: draft.id }));
  const [recovered, setRecovered] = useState(false);
  const staleOrigin = !!stored.current?.[overlay] && JSON.stringify(stored.current.origin) !== originKey && !recovered;
  const [writing, setWriting] = useState<PrivateFeedbackReviewCarry>(() => structuredClone(stored.current?.[overlay] ?? {
    content: { message: '', next_step: '', ...target },
  }));
  const writingRef = useRef(writing);
  const edited = useRef(false);
  const [expanded, setExpanded] = useState(false);
  const [review, setReview] = useState<PrivateFeedbackReviewCarry | null>(null);
  const [storageError, setStorageError] = useState('');
  const [navigating, setNavigating] = useState(false);
  const handoff = useRef(false);
  const life = useRef(0);
  const locked = operationPending || navigating;
  const currentCarry = () => {
    const latest = readReviewCarry(owner, origin);
    return latest && JSON.stringify(latest.origin) === originKey ? latest : null;
  };
  const snapshot = (): PrivateFeedbackReviewCarry => {
    const latest = currentCarry()?.[overlay];
    // The native return owns attachments and its locator. Read it at the moment
    // of persistence, never replace it with a render-time copy.
    const current = latest ?? writingRef.current;
    return { ...current, content: { ...current.content,
      message: writingRef.current.content.message ?? '', next_step: writingRef.current.content.next_step ?? '',
      ...target,
    } };
  };
  const persist = (value = snapshot(), recordingIntent?: 'private' | 'public') => {
    if (staleOrigin || !isCurrentDraftContext(owner)) return false;
    const latest = currentCarry();
    const saved = saveReviewCarry(owner, {
      kind: 'feedback', origin, edits: latest?.edits ?? stored.current?.edits ?? {},
      ...(latest?.video ? { video: latest.video } : {}), [overlay]: value, ...(recordingIntent ? { recordingIntent } : {}),
    });
    if (!saved) setStorageError('Your writing is still here, but it could not be retained for return. Try again before continuing.');
    else setStorageError('');
    return saved;
  };
  const persistRef = useRef(persist);
  persistRef.current = persist;
  useEffect(() => {
    const save = () => { if (edited.current) persistRef.current(); };
    window.addEventListener('pagehide', save);
    return () => { save(); window.removeEventListener('pagehide', save); };
  }, []);
  useEffect(() => {
    life.current += 1; handoff.current = false; setNavigating(false); setReview(null);
    if (active && !staleOrigin) {
      const value = snapshot(); writingRef.current = value; setWriting(value);
    }
    return () => { life.current += 1; };
  // Re-entry only hydrates attachment context; it never restores reviewed authority.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);
  useEffect(() => { setReview(null); }, [audience]);
  const edit = (field: 'message' | 'next_step', value: string) => {
    if (locked || staleOrigin) return;
    const next = snapshot(); next.content = { ...next.content, [field]: value };
    writingRef.current = next; setWriting(next); edited.current = true; setReview(null); persist(next);
  };
  const message = writing.content.message ?? '', nextStep = writing.content.next_step ?? '';
  const messageLength = feedbackProseLength(message), nextLength = feedbackProseLength(nextStep);
  const valid = messageLength > 0 && isFeedbackProseWithinLimit('message', message) && isFeedbackProseWithinLimit('next_step', nextStep);
  const recordingPending = !!writing.recording_entry_id && !writing.recording;
  const prepareReview = () => {
    if (!active || disabled || locked || staleOrigin || !valid) return;
    const next = snapshot();
    if (next.recording_entry_id && !next.recording || !persist(next)) return;
    writingRef.current = next; setWriting(next); setReview(structuredClone(next));
  };
  const approve = () => {
    if (!active || !review || disabled || locked || staleOrigin) return;
    const latest = snapshot();
    if (JSON.stringify(latest) !== JSON.stringify(review)) {
      writingRef.current = latest; setWriting(latest); setReview(null);
      setStorageError('Linked context changed. Review the complete Feedback again.'); return;
    }
    if (persist(latest)) { if (privateMode) onApprovePrivate(structuredClone(review)); else onApprovePublic(structuredClone(review)); }
  };
  const openRecording = async () => {
    if (!active || locked || staleOrigin || handoff.current || !persist(snapshot(), privateMode ? 'private' : 'public')) return;
    const generation = life.current;
    handoff.current = true; setNavigating(true); setReview(null);
    try { await navigate({ to: '/coach/review', search: { source: 'video', inbox_capture_id: origin.captureId } }); }
    catch (e) { if (life.current === generation && isCurrentDraftContext(owner)) setStorageError(errorMessage(e)); }
    finally { if (life.current === generation && isCurrentDraftContext(owner)) { handoff.current = false; setNavigating(false); } }
  };
  const removeRecording = () => {
    if (locked || staleOrigin) return;
    const next = snapshot();
    if (!next.recording) return; // An unresolved native attempt is never cleared here.
    const pair = next.recording.recording_resource;
    next.content = { ...next.content, resource_links: next.content.resource_links?.filter(pin =>
      pin.resource_id.toLowerCase() !== pair.resource_id.toLowerCase() || pin.revision_id.toLowerCase() !== pair.revision_id.toLowerCase()) };
    delete next.recording; delete next.recording_entry_id;
    if (persist(next)) { writingRef.current = next; setWriting(next); edited.current = true; setReview(null); }
  };
  const earlier = stored.current?.edits ?? {};
  const referenceFields = Object.entries(draft.proposed_fields).map(([field, value]) => [field.replaceAll('_', ' '), value]);
  return <section className="grid min-w-0 gap-4 border-t border-rule pt-4" aria-label={review ? privateMode ? 'Review private Feedback' : 'Review Feedback publication' : 'Prepare your feedback'}>
    <h4 className="font-display text-lg">{review ? privateMode ? 'Review private Feedback' : 'Review Feedback publication' : 'Prepare your feedback'}</h4>
    <p className="text-sm text-ink-muted">{privateMode ? 'Only me · Saving privately does not publish to players or families.' : 'Write the exact Feedback to publish. The original proposal is reference context only.'}</p>
    {storageError && <p role="alert">{storageError}</p>}
    {staleOrigin && <div role="status" className="grid gap-2">
      <p>A newer proposal or confirmation is available. Your earlier writing is retained separately; it has not been reviewed for this version.</p>
      {stored.current?.[overlay]?.recording_entry_id
        ? <p>The recording attempt belongs to the original review. Return to that target to resolve it; it cannot be moved to this review.</p>
        : <Button variant="outline" disabled={locked} onClick={() => {
          const next: PrivateFeedbackReviewCarry = { content: { message, next_step: nextStep, ...target } };
          if (saveReviewCarry(owner, { kind: 'feedback', origin, edits: earlier, [overlay]: next })) {
            setRecovered(true); writingRef.current = next; setWriting(next); edited.current = true; setReview(null);
          } else setStorageError('Your retained writing could not be saved for this review. Try again.');
        }}>Apply my retained human writing to this review</Button>}
    </div>}
    {!review ? <>
      <Button variant="outline" className="h-auto justify-between whitespace-normal py-3 text-left" aria-expanded={expanded} onClick={() => setExpanded(value => !value)}>
        <span>Original proposal and earlier edits<span className="block text-xs font-normal text-ink-muted">Read only · full text retained</span></span>
        <ChevronRight aria-hidden="true" className={`size-4 shrink-0 ${expanded ? 'rotate-90' : ''}`} />
      </Button>
      {expanded && <section aria-label="Original proposal and earlier edits" className="grid min-w-0 gap-4 rounded-panel border border-rule p-4 [overflow-wrap:anywhere]">
        <p className="text-sm text-ink-muted">Reference context only. These fields are not copied into your Message or treated as confirmed facts.</p>
        <h5 className="font-display">Original proposal</h5>
        {referenceFields.map(([label, value]) => <div key={label}><h6 className="text-xs font-semibold">{label}</h6><p className="whitespace-pre-wrap text-sm">{value || 'No text provided.'}</p></div>)}
        {draft.schema_version === 'coaching-draft.v4' && <ProposalEvidence output={draft.structured_output} players={players} />}
        <h5 className="font-display">Earlier human edits</h5>
        {Object.keys(earlier).length === 0 ? <p className="text-sm">No earlier human edits.</p> : Object.entries(earlier).map(([field, value]) => <div key={field}><h6 className="text-xs font-semibold">{field.replaceAll('_', ' ')}</h6><p className="whitespace-pre-wrap text-sm">{value || 'No text entered.'}</p></div>)}
      </section>}
      <label className="grid gap-2 text-sm">Message
        <textarea aria-label="Message" disabled={locked || staleOrigin} value={message} rows={6} className="w-full min-w-0 rounded-xl border border-rule bg-surface p-3 text-ink" onChange={e => edit('message', e.target.value)} aria-invalid={messageLength > feedbackProseLimits.message} />
        <span className="text-xs text-ink-muted">{messageLength} / {feedbackProseLimits.message}</span>
      </label>
      {messageLength > feedbackProseLimits.message && <p role="alert">Message is over 4,000 characters. Your full writing is retained; edit it to continue.</p>}
      <label className="grid gap-2 text-sm">Next step (optional)
        <textarea aria-label="Next step (optional)" disabled={locked || staleOrigin} value={nextStep} rows={3} className="w-full min-w-0 rounded-xl border border-rule bg-surface p-3 text-ink" onChange={e => edit('next_step', e.target.value)} aria-invalid={nextLength > feedbackProseLimits.next_step} />
        <span className="text-xs text-ink-muted">{nextLength} / {feedbackProseLimits.next_step}</span>
      </label>
      {nextLength > feedbackProseLimits.next_step && <p role="alert">Next step is over 2,000 characters. Your full writing is retained; edit it to continue.</p>}
      {recordingPending && <p role="status">The original recording save or attachment still needs resolution. Your writing is retained; return to that attempt before approval.</p>}
      {writing.recording && <p className="text-sm">Independently saved recording · {writing.recording.video_reference.slot} at {writing.recording.video_reference.timestamp_seconds.toFixed(1)} seconds. This does not establish fact occurrence. <Button variant="ghost" disabled={locked || staleOrigin} onClick={removeRecording}>Remove recording attachment</Button></p>}
      <Button variant="outline" disabled={locked || staleOrigin} onClick={() => void openRecording()}>{recordingPending ? 'Return to recording in Review' : 'Add video evidence in Review'}</Button>
      <Button disabled={disabled || locked || staleOrigin || !valid || recordingPending} onClick={prepareReview}>{privateMode ? 'Review private Feedback' : 'Review Feedback publication'}</Button>
    </> : <>
      <article className="grid gap-4 rounded-panel bg-elevated p-4 [overflow-wrap:anywhere]">
        <div><h5 className="text-xs font-semibold">Message</h5><p className="whitespace-pre-wrap">{review.content.message}</p></div>
        {!!review.content.next_step && <div><h5 className="text-xs font-semibold">Next step</h5><p className="whitespace-pre-wrap">{review.content.next_step}</p></div>}
        {!!review.content.resource_links?.length && <div><h5 className="text-xs font-semibold">Exact Resource links</h5>{review.content.resource_links.map(pin => <p key={pin.resource_id} className="text-xs">Resource {pin.resource_id} · revision {pin.revision_id}</p>)}</div>}
        {!!review.content.goal_ids?.length && <p className="text-sm">Linked Goals: {review.content.goal_ids.join(', ')}</p>}
        {review.content.associated_match_id && <p className="text-sm">Match: {review.content.associated_match_id}</p>}
        {review.content.ref_video_id && <p className="text-sm">External reference: {review.content.ref_video_label || review.content.ref_video_title || review.content.ref_video_provider} · {review.content.ref_video_id}</p>}
      </article>
      {privateMode ? <p className="text-sm">Only you. No player or family recipients. Confirmed facts and suggested actions remain separate from this writing.</p> : <section aria-label="Reviewed publication audience" className="grid gap-2">
        <h5 className="font-display">Who will receive this?</h5>
        <p>{target.subject_mode === 'team' ? 'Team-wide Feedback audience' : 'Selected players and their linked family'}</p>
        <p className="text-sm">Factual observed players: {confirmation.selection.player_ids.map(id => players.find(player => player.id === id)?.display_name ?? 'Player unavailable').join(', ')}. These factual subjects remain separate from the publication audience.</p>
        {audience ? <ul>{audience.recipients.map(recipient => <li key={recipient.user_id}>{recipient.display_name || 'Recipient name unavailable'} · {recipient.relationships.join(', ')}</li>)}</ul> : <p>Recipients unavailable. Refresh the complete preview before publishing.</p>}
        <p className="text-xs text-ink-muted">Publish directly for these recipients. There is no intermediate private save.</p>
      </section>}
      <div className="flex flex-wrap justify-between gap-2"><Button variant="outline" onClick={() => setReview(null)}>Back to writing</Button><Button disabled={disabled || locked} onClick={approve}>{privateMode ? 'Approve and save privately' : 'Approve and publish Feedback'}</Button></div>
    </>}
  </section>;
}
